/**
 * Logger utility
 * Replaces console.logs with environment-aware logging
 * Only logs in development mode
 */

interface Logger {
  debug: (...args: unknown[]) => void;
  info: (...args: unknown[]) => void;
  warn: (...args: unknown[]) => void;
  error: (...args: unknown[]) => void;
}

/**
 * Create a logger that only logs in development mode
 */
const createLogger = (prefix: string): Logger => {
  const isDev = import.meta.env.DEV;

  return {
    debug: (...args: unknown[]) => {
      if (isDev) {
        console.debug(`[${prefix}]`, ...args);
      }
    },
    info: (...args: unknown[]) => {
      if (isDev) {
        console.log(`[${prefix}]`, ...args);
      }
    },
    warn: (...args: unknown[]) => {
      if (isDev) {
        console.warn(`[${prefix}]`, ...args);
      }
    },
    error: (...args: unknown[]) => {
      // Always log errors, even in production
      console.error(`[${prefix}]`, ...args);
    },
  };
};

/**
 * Default logger for general use
 */
export const logger = createLogger('App');

/**
 * Service-specific loggers
 */
export const serviceLogger = createLogger('Service');
export const apiLogger = createLogger('API');
export const authLogger = createLogger('Auth');

/**
 * Log API requests (only in dev mode)
 */
export const logApiRequest = (method: string, url: string, data?: unknown): void => {
  if (import.meta.env.DEV) {
    apiLogger.info(`${method} ${url}`, data ? { data } : '');
  }
};

/**
 * Log API responses (only in dev mode)
 */
export const logApiResponse = (method: string, url: string, status: number, data?: unknown): void => {
  if (import.meta.env.DEV) {
    apiLogger.info(`${method} ${status} ${url}`, data ? { data } : '');
  }
};

/**
 * Log errors (always logged, even in production)
 */
export const logError = (message: string, error?: unknown): void => {
  logger.error(message, error);
};

